import { D, F, G } from "@mobily/ts-belt";
import PropTypes from "prop-types";
import * as React from "react";
import FilterSection, {
  FilterSectionSkeleton,
} from "../../BrowseProducts/components/FilterSection";
import useCategoryMap from "../hooks/useCategoryMap";
import { defaultFilter, useFilters } from "../utils";
import CategoryFilter from "./CategoryFilter";
import s from "./FilterPanel.module.css";

const getId = D.get("id");
const getLabel = D.get("label");

export default function FilterPanel({ filter, setFilter, showFilters }) {
  const { filters, isLoading } = useFilters();
  const categoryMap = useCategoryMap();

  const setFilterAndRestoreRank = React.useCallback(
    (fn) => setFilter((filter) => D.merge(fn(filter), { cursor: "" })),
    [setFilter],
  );

  const handleClearAll = React.useCallback(() => {
    setFilter(defaultFilter);
  }, [setFilter]);

  const makeSetSelected = React.useCallback(
    (filterKey) =>
      ({ value, key }) => {
        if (value) {
          setFilterAndRestoreRank(D.update(filterKey, D.set(key, value)));
        } else {
          setFilterAndRestoreRank(D.update(filterKey, D.deleteKey(key)));
        }
      },
    [setFilterAndRestoreRank],
  );

  /* Auto-populate: when a category is selected with empty subcategories,
     fill in all its subcategories */
  React.useEffect(() => {
    if (D.isEmpty(filter.category)) {
      return;
    }

    const entries = Object.entries(filter.category);

    if (entries.length !== 1) {
      return;
    }

    const [categoryId, value] = entries[0];

    if (!G.isObject(value) || D.isNotEmpty(value)) {
      return;
    }

    if (D.isEmpty(categoryMap)) {
      return;
    }

    const category = categoryMap[categoryId];

    if (
      !G.isObject(category) ||
      D.isEmpty(category) ||
      !Array.isArray(category.subcategories) ||
      !category.subcategories.length
    ) {
      return;
    }

    setFilterAndRestoreRank(
      D.merge({
        category: {
          [categoryId]: category.subcategories.reduce((acc, sub) => {
            acc[sub.id] = true;
            return acc;
          }, {}),
        },
      }),
    );
  }, [filter, categoryMap, setFilterAndRestoreRank]);

  const isFiltered = !F.equals(filter, defaultFilter);

  return (
    <aside className={s.panel} aria-label="Shop filters">
      <div className={s.panelHeader}>
        <h2 className={s.panelTitle}>Filters</h2>
        {isFiltered && (
          <button type="button" className={s.clearBtn} onClick={handleClearAll}>
            Clear
          </button>
        )}
      </div>

      <div
        className={`${s.collapse}${showFilters ? ` ${s.collapseOpen}` : ""}`}
      >
        <div className={s.collapseInner}>
          {isLoading ? (
            <>
              <FilterSectionSkeleton width="55%" />
              <FilterSectionSkeleton width="60%" />
              <FilterSectionSkeleton width="50%" />
            </>
          ) : (
            <>
              {D.isNotEmpty(categoryMap) && (
                <CategoryFilter
                  expandedByDefault
                  categories={categoryMap}
                  selected={filter.category}
                  setSelected={(setter) => {
                    const newValue =
                      typeof setter === "function"
                        ? setter(filter.category)
                        : setter;
                    if (newValue) {
                      setFilterAndRestoreRank(D.set("category", newValue));
                    } else {
                      setFilterAndRestoreRank(D.set("category", {}));
                    }
                  }}
                />
              )}

              {Boolean(filters.franchises.length) && (
                <FilterSection
                  expandedByDefault
                  options={filters.franchises}
                  label="Franchises"
                  getOptionLabel={getLabel}
                  getOptionValue={getId}
                  selected={filter.franchise}
                  setSelected={makeSetSelected("franchise")}
                />
              )}

              {Boolean(filters.shopTypes.length) && (
                <FilterSection
                  search={false}
                  options={filters.shopTypes}
                  label="Shop Types"
                  getOptionLabel={getLabel}
                  getOptionValue={getId}
                  selected={filter.type}
                  setSelected={makeSetSelected("type")}
                />
              )}
            </>
          )}

          {isFiltered && (
            <button
              type="button"
              className={s.clearAllBtn}
              onClick={handleClearAll}
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>
    </aside>
  );
}

FilterPanel.propTypes = {
  filter: PropTypes.shape({
    franchise: PropTypes.object,
    type: PropTypes.object,
    category: PropTypes.object,
  }).isRequired,
  setFilter: PropTypes.func.isRequired,
  showFilters: PropTypes.bool.isRequired,
};
